import { useCallback, useEffect, useRef, useState } from "react";
import { getPostComments } from "../services/postService";

const mergeComments = (current, incoming) => {
  const commentsById = new Map();

  [...current, ...incoming].forEach((comment) => {
    commentsById.set(comment.id, comment);
  });

  return [...commentsById.values()];
};

function useComments(post) {
  const [comments, setComments] = useState(post.comments || []);
  const [commentCount, setCommentCount] = useState(post.comments?.length || 0);
  const [isLoadingComments, setIsLoadingComments] = useState(false);
  const [commentsError, setCommentsError] = useState("");
  const requestId = useRef(0);

  useEffect(() => {
    requestId.current += 1;
    setComments(post.comments || []);
    setCommentCount(post.comments?.length || 0);
    setCommentsError("");
  }, [post]);

  const refreshComments = useCallback(async () => {
    const currentRequest = ++requestId.current;
    setIsLoadingComments(true);
    setCommentsError("");

    try {
      const response = await getPostComments(post.id);
      if (currentRequest !== requestId.current) return;

      const incoming = response.comments || [];
      setComments((current) => mergeComments(current, incoming));
      setCommentCount(incoming.length);
    } catch (error) {
      if (currentRequest === requestId.current) {
        setCommentsError("Não foi possível carregar os comentários.");
      }
    } finally {
      if (currentRequest === requestId.current) setIsLoadingComments(false);
    }
  }, [post.id]);

  const addComment = useCallback((createdComment) => {
    if (!createdComment) return;

    setComments((current) => mergeComments(current, [createdComment]));
    setCommentCount((current) => current + 1);
  }, []);

  return {
    comments,
    commentCount,
    isLoadingComments,
    commentsError,
    refreshComments,
    addComment,
  };
}

export { mergeComments, useComments };
